/**
 * ApiQuotaCard — R3-S3 (F3.2)
 *
 * Zeigt das Monatskontingent der Public API (v1-Paritaet zu
 * app/views/home/api.haml: "API-Requests this month: X/Y").
 *
 * Semantik laut Runde-3-Entscheidung: angezeigt wird der VERBRAUCH
 * (quota_used/quota_limit) plus eine Zusatzzeile mit dem Rest — v1 zeigte den
 * Restzaehler unter der Beschriftung "Requests", was faktisch irrefuehrend war.
 *
 * Zustaende: loading | error | kein API-Zugang (Upsell) | normal.
 * Farbschwellen: >= 80 % Warnung, >= 100 % Ueberschreitung.
 */
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useApiUsage } from './useApiUsage'

const WARN_THRESHOLD = 80
const DANGER_THRESHOLD = 100

function formatDate(iso: string, locale: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('developer')
  return (
    <section
      className="card card-pad"
      data-testid="api-quota-card"
      aria-label={t('apiQuota.title')}
      style={{ marginBottom: 18 }}
    >
      <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>{t('apiQuota.title')}</h2>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--text-subtle)' }}>
        {t('apiQuota.scopeHint')}
      </p>
      {children}
    </section>
  )
}

export function ApiQuotaCard() {
  const { t, i18n } = useTranslation('developer')
  const { usage, isLoading, isError, refetch } = useApiUsage()

  if (isLoading) {
    return (
      <Shell>
        <div
          data-testid="api-quota-loading"
          aria-busy="true"
          aria-label={t('apiQuota.loading')}
          style={{
            height: 48,
            borderRadius: 8,
            background: 'color-mix(in srgb, var(--text) 8%, transparent)',
            animation: 'ht-skeleton-pulse 1.5s ease-in-out infinite',
          }}
        />
      </Shell>
    )
  }

  if (isError || !usage) {
    return (
      <Shell>
        <div role="alert" data-testid="api-quota-error">
          <p style={{ margin: '0 0 12px', fontSize: 13 }}>{t('apiQuota.error')}</p>
          <button type="button" className="btn btn-sm btn-primary" onClick={refetch}>
            {t('common:actions.retry')}
          </button>
        </div>
      </Shell>
    )
  }

  if (!usage.api_access || usage.quota_limit <= 0) {
    return (
      <Shell>
        <div data-testid="api-quota-upsell">
          <p style={{ margin: '0 0 12px', fontSize: 13 }}>{t('apiQuota.noAccess')}</p>
          <Link to="/plans" className="btn btn-sm btn-primary" data-testid="api-quota-upsell-cta">
            {t('apiQuota.noAccessCta')}
          </Link>
        </div>
      </Shell>
    )
  }

  const pct = usage.quota_used_pct
  const level = pct >= DANGER_THRESHOLD ? 'danger' : pct >= WARN_THRESHOLD ? 'warn' : 'ok'
  const barColor =
    level === 'danger' ? 'var(--danger)' : level === 'warn' ? 'var(--warning)' : 'var(--primary)'

  return (
    <Shell>
      <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }} data-testid="api-quota-counter">
        {t('apiQuota.requestsThisMonth', {
          used: usage.quota_used.toLocaleString(i18n.language),
          limit: usage.quota_limit.toLocaleString(i18n.language),
        })}
      </p>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-subtle)' }} data-testid="api-quota-remaining">
        {t('apiQuota.remaining', { count: usage.quota_remaining })}
      </p>

      <div
        role="progressbar"
        aria-valuenow={Math.min(pct, 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('apiQuota.progressAria', { pct })}
        data-testid="api-quota-progress"
        data-level={level}
        style={{
          height: 8,
          borderRadius: 999,
          background: 'color-mix(in srgb, var(--text) 10%, transparent)',
          overflow: 'hidden',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: `${Math.min(Math.max(pct, 0), 100)}%`,
            height: '100%',
            background: barColor,
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {level === 'warn' && (
        <p role="status" data-testid="api-quota-warning" style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--warning)' }}>
          {t('apiQuota.warnThreshold')}
        </p>
      )}
      {level === 'danger' && (
        <p role="alert" data-testid="api-quota-exceeded" style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--danger)' }}>
          {t('apiQuota.exceeded')}
        </p>
      )}

      {/* Story 5.2: Upgrade-Hinweis ab 80 % Verbrauch (Design-Vorlage developer.html) */}
      {(level === 'warn' || level === 'danger') && (
        <p data-testid="api-quota-upgrade-hint" style={{ margin: '0 0 10px', fontSize: 12 }}>
          {t('apiQuota.upgradeHint')}{' '}
          <Link
            to="/plans"
            data-testid="api-quota-upgrade-link"
            style={{ color: 'var(--primary)', fontWeight: 500 }}
          >
            {t('apiQuota.upgradeCta')}
          </Link>
        </p>
      )}

      <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 12, color: 'var(--text-subtle)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <li data-testid="api-quota-resets">
          {t('apiQuota.resetsOn', { date: formatDate(usage.resets_at, i18n.language) })}
        </li>
        <li data-testid="api-quota-rate-limit">
          {t('apiQuota.rateLimit', { rate: usage.rate_limit_per_hour.toLocaleString(i18n.language) })}
        </li>
        <li data-testid="api-quota-plan">{t('apiQuota.plan', { plan: usage.plan })}</li>
      </ul>
    </Shell>
  )
}
